import { z } from '@hono/zod-openapi';

import { ChannelSchema, InstantSchema } from './common';
import { CONTRACT_VERSION, EVENT_CATALOG_VERSION } from './versions';

const empty = z.object({}).strict();
const result = z.enum(['success', 'failed', 'cancelled']);
const source = z.enum(['manual', 'room_prefill', 'onboarding_import', 'copy_draft', 'plan']);
const durationBucket = z.enum(['lt_5m', '5_24m', '25_59m', '60_119m', 'gte_120m']);
const countBucket = z.enum(['0', '1_9', '10_49', '50_199', 'gte_200']);
const roundBucket = z.enum(['1', '2', '3', '4_plus']);
const dDayBucket = z.enum(['past', 'today', '1_7', '8_30', '31_90', '91_plus', 'unknown']);
const sizeBucket = z.enum(['lt_1mb', '1_10mb', '10_50mb']);
const sessionBudget = z.enum(['15', '30', '60']);

export const EVENT_PROPERTY_SCHEMAS = {
  // 워크스페이스 표준 4이벤트. 이 카탈로그의 첫 이벤트가 `onboarding_started`라
  // 사용자가 행동하기 전에는 아무것도 발화하지 않았고, 그래서 첫 방문 cohort가 통째로
  // 빠졌다(실측 2026-08-12: 첫 방문 계측 요청 0건). 제품 고유 이벤트를 대체하지 않고 얹는다.
  // 정본: workspace/docs/munops/ops/release-integrity.md 「표준 계측 이벤트」
  return_visit: z.object({ daysSinceFirstOpen: z.enum(['1', '2_7', '8_30', '31_plus']) }).strict(),
  share_completed: z
    .object({
      surface: z.enum([
        'progress_card',
        'cert_text',
        'achievement',
        'calendar',
        'exam_page',
        'session_card',
      ]),
      result,
    })
    .strict(),
  pass_interest: z.object({ from: z.enum(['pass_page', 'workbook_limit']) }).strict(),
  weekly_three_days: z.object({}).strict(),
  app_open: z.object({}).strict(),
  landing_view: z.object({}).strict(),
  activation_start: z.object({}).strict(),
  activation_done: z.object({}).strict(),
  onboarding_started: z.object({ entry: z.enum(['fresh', 'resume']) }).strict(),
  exam_selected: z
    .object({
      selection: z.enum(['preset', 'custom']),
      dateStatus: z.enum(['dated', 'unknown']).optional(),
      examCount: z.enum(['1', '2', '3', '4_plus']).optional(),
    })
    .strict(),
  first_log_saved: z.object({ source }).strict(),
  onboarding_completed: z.object({ durationBucket }).strict(),
  study_log_saved: z.object({ source, roundBucket, dDayBucket }).strict(),
  round_completed: z.object({ roundBucket, dDayBucket }).strict(),
  plan_confirmed: z.object({ dDayBucket, result }).strict(),
  plan_recalculated: z.object({ dDayBucket, result }).strict(),
  wrong_note_saved: z.object({ hasPhoto: z.boolean(), countBucket }).strict(),
  review_started: z
    .object({ mode: z.enum(['random', 'subject', 'unmastered']), countBucket })
    .strict(),
  today_session_started: z
    .object({ budget: sessionBudget, planCountBucket: countBucket, reviewCountBucket: countBucket })
    .strict(),
  wrong_note_mastered: z.object({ countBucket }).strict(),
  export_completed: z
    .object({ result, sizeBucket, schemaVersion: z.number().int().positive() })
    .strict(),
  backup_attempted: z
    .object({ result, sizeBucket, schemaVersion: z.number().int().positive() })
    .strict(),
  restore_completed: z
    .object({ result, sizeBucket, schemaVersion: z.number().int().positive() })
    .strict(),
  room_joined: z
    .object({
      roomType: z.enum(['official', 'private']),
      mode: z.enum(['full', 'participation_light']),
    })
    .strict(),
  room_session_finished: z
    .object({
      roomType: z.enum(['official', 'private']),
      durationBucket,
      outcome: z.enum(['prefill_saved', 'discarded', 'disconnected', 'kicked']),
    })
    .strict(),
  room_reported: z.object({ roomType: z.enum(['official', 'private']), result }).strict(),
  paywall_viewed: z
    .object({
      trigger: z.enum(['plan', 'review', 'ai', 'pass_page', 'workbook_limit']),
      productId: z.string().min(1).max(80),
    })
    .strict(),
  purchase_started: z.object({ productId: z.string().min(1).max(80) }).strict(),
  purchase_completed: z.object({ productId: z.string().min(1).max(80), result }).strict(),
  purchase_restored: z.object({ productId: z.string().min(1).max(80), result }).strict(),
} as const;

export const EVENT_NAMES = Object.keys(EVENT_PROPERTY_SCHEMAS) as Array<
  keyof typeof EVENT_PROPERTY_SCHEMAS
>;
export type ProductEventName = (typeof EVENT_NAMES)[number];
export type ProductEventProperties<Name extends ProductEventName> = z.infer<
  (typeof EVENT_PROPERTY_SCHEMAS)[Name]
>;

const propertyValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const ProductEventSchema = z
  .object({
    catalogVersion: z.union([z.literal(1), z.literal(2), z.literal(EVENT_CATALOG_VERSION)]),
    contractVersion: z.literal(CONTRACT_VERSION),
    name: z.enum(EVENT_NAMES),
    occurredAt: InstantSchema,
    channel: ChannelSchema,
    build: z.string().min(1).max(100),
    properties: z.record(z.string(), propertyValue),
  })
  .strict()
  .superRefine((event, context) => {
    const parsed = EVENT_PROPERTY_SCHEMAS[event.name].safeParse(event.properties);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        context.addIssue({
          code: 'custom',
          path: ['properties', ...issue.path],
          message: issue.message,
        });
      }
    }
  })
  .openapi('ProductEvent');

export const EventBatchSchema = z
  .object({ events: z.array(ProductEventSchema).min(1).max(50) })
  .strict()
  .refine((batch) => new TextEncoder().encode(JSON.stringify(batch)).byteLength <= 65_536, {
    message: 'Event batch exceeds 64KiB',
  })
  .openapi('EventBatch');

export type ProductEvent = z.infer<typeof ProductEventSchema>;

export const EventAcceptedSchema = z
  .object({
    accepted: z.number().int().min(1).max(50),
    requestId: z.uuid(),
  })
  .strict()
  .openapi('EventAccepted');

export const EVENT_EXAMPLE_PROPERTIES: Record<ProductEventName, Record<string, unknown>> = {
  return_visit: { daysSinceFirstOpen: '2_7' },
  share_completed: { surface: 'cert_text', result: 'success' },
  pass_interest: { from: 'pass_page' },
  weekly_three_days: {},
  app_open: {},
  landing_view: {},
  activation_start: {},
  activation_done: {},
  onboarding_started: { entry: 'fresh' },
  exam_selected: { selection: 'preset' },
  first_log_saved: { source: 'manual' },
  onboarding_completed: { durationBucket: '5_24m' },
  study_log_saved: { source: 'manual', roundBucket: '1', dDayBucket: '31_90' },
  round_completed: { roundBucket: '1', dDayBucket: '31_90' },
  plan_confirmed: { dDayBucket: '31_90', result: 'success' },
  plan_recalculated: { dDayBucket: '8_30', result: 'success' },
  wrong_note_saved: { hasPhoto: true, countBucket: '10_49' },
  review_started: { mode: 'unmastered', countBucket: '10_49' },
  today_session_started: { budget: '30', planCountBucket: '1_9', reviewCountBucket: '1_9' },
  wrong_note_mastered: { countBucket: '1_9' },
  export_completed: { result: 'success', sizeBucket: '1_10mb', schemaVersion: 1 },
  backup_attempted: { result: 'success', sizeBucket: '1_10mb', schemaVersion: 1 },
  restore_completed: { result: 'success', sizeBucket: '1_10mb', schemaVersion: 1 },
  room_joined: { roomType: 'official', mode: 'full' },
  room_session_finished: {
    roomType: 'official',
    durationBucket: '25_59m',
    outcome: 'prefill_saved',
  },
  room_reported: { roomType: 'official', result: 'success' },
  paywall_viewed: { trigger: 'ai', productId: 'wrong_note_ai' },
  purchase_started: { productId: 'wrong_note_ai' },
  purchase_completed: { productId: 'wrong_note_ai', result: 'success' },
  purchase_restored: { productId: 'exam_slots_over_three', result: 'success' },
};

export const EMPTY_EVENT_SCHEMA = empty;
